/*
 * Metric-explanation bottom sheet — opened by the "?" button on a stats card.
 * Lists each metric with a short description and a higher/lower-is-better tag.
 * FIP-themed; slides up from the bottom over a dimmed backdrop.
 */

import type React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, X } from 'lucide-react';

export interface InfoMetric {
  name: string;
  description: string;
  higherIsBetter: boolean;
}

export const InfoSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  metrics: InfoMetric[];
}> = ({ open, onClose, title, metrics }) => {
  const { t } = useTranslation('tournament');
  // Render to <body> via a portal: the sheet is `position: fixed`, but the card
  // it lives in sits inside transformed ancestors (the carousel track / layout
  // animations), which would otherwise become its containing block and push the
  // sheet far below the viewport. The `tr-root` wrapper keeps the scoped CSS
  // variables resolving.
  return createPortal(
    <div className="tr-root">
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              className="info-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <motion.div
              className="info-sheet"
              role="dialog"
              aria-label={title}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            >
              <div className="info-sheet-handle" />
              <div className="info-sheet-head">
                <span className="info-sheet-title">{title}</span>
                <button
                  type="button"
                  className="info-sheet-close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X size={18} strokeWidth={2.2} />
                </button>
              </div>
              <div className="info-sheet-list">
                {metrics.map((m) => (
                  <div className="info-sheet-item" key={m.name}>
                    <div className="info-sheet-name">{m.name}</div>
                    <p className="info-sheet-desc">{m.description}</p>
                    <span
                      className={`info-sheet-tag ${
                        m.higherIsBetter ? 'up' : 'down'
                      }`}
                    >
                      {m.higherIsBetter ? (
                        <TrendingUp size={12} strokeWidth={2.4} />
                      ) : (
                        <TrendingDown size={12} strokeWidth={2.4} />
                      )}
                      {
                        t(
                          `label.${
                            m.higherIsBetter
                              ? 'higherIsBetter'
                              : 'lowerIsBetter'
                          }` as never,
                        ) as string
                      }
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>,
    document.body,
  );
};
